<script lang="ts">
  import _memoize from 'lodash/memoize';
  import _uniq from 'lodash/uniq';
  import * as d3 from 'd3';
  import { geoOrthographic, geoPath, geoGraticule } from 'd3-geo';
  import type { ExtendedFeature, ExtendedFeatureCollection } from 'd3-geo';
  import { feature } from 'topojson-client';
  import type { Topology } from 'topojson-specification';
  import type { ChartGeospatial } from '$lib/sanity/schemas';
  import geoData from './assets/world-countries.json';
  import ChartLayout from './components/ChartLayout.svelte';
  import Legend from './components/Legend.svelte';
  import Table from './components/Table.svelte';
  import ToolTip, { toolTipStore } from './components/ToolTip.svelte';
  import { addGradients, setUpChart } from './utils/chart';
  import { getPalette, type Color } from './utils/color';
  import { categorizeChartItems, formatDocumentId } from './utils/data';
  import { interactionStore } from './utils/interactions';
  import {
    useEncodeDataAttribute,
    type ContentSourceMap,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import { sanityStudioURL } from '$lib/client';

  type GeoProperties = {
    name: string;
    'Alpha-2': string;
  };

  interface Props {
    document: ChartGeospatial;
    sourceMap: ContentSourceMap;
    path?: StudioPathLike;
    gridColspan: number;
  }

  let { document, sourceMap, path = [], gridColspan }: Props = $props();

  let encodeDataAttribute = $derived(useEncodeDataAttribute(document, sourceMap, sanityStudioURL));

  const { _id, alt, chart = null, footnote = null, headline = '', subhead = '' } = document;

  const { items = [] } = chart || {};
  const chartCategories = categorizeChartItems(items);
  const categories = _uniq([...chartCategories.primary, ...chartCategories.secondary]);
  const id = formatDocumentId(_id);
  const store = interactionStore(id);
  const toolTip = toolTipStore();
  const colorPalette = getPalette('theme-categorical-2', 2);
  const countries = items.map(({ country }) => country);
  const sensitivity = 75;
  const tableProps = {
    header: ['Country', 'Group'],
    rows: items.map((item) => {
      const group = [item.primaryCategory];

      if (item.secondaryCategory) group.push(item.secondaryCategory);

      return [item.country, group.join(', ')];
    })
  };
  const categoryColors = categories.reduce((result: { [key: string]: Color }, category) => {
    const index = categories.indexOf(category);

    result[category] = colorPalette.colors[index] || {};

    return result;
  }, {});
  const countryColors: { [key: string]: string } = {};
  const gradients: Color[] = [];
  const getGradientId = (colors: Color[]) => colors.map((color) => color.id).join('-');
  const isSelected = _memoize((countryName) => countries.includes(countryName));
  const clamp = (number: number, min: number, max: number) => {
    return Math.min(Math.max(number, min), max);
  };

  // Build colors and gradients for each country
  items.forEach((item) => {
    const color1 = categoryColors[item.primaryCategory];

    if (item.secondaryCategory) {
      const color2 = categoryColors[item.secondaryCategory];
      const id = getGradientId([color1, color2]);

      gradients.push({
        id,
        colors: [color1?.color || '', color2?.color || ''],
        angle: 90
      });
      countryColors[item.country] = `url(#${id})`;
    } else {
      countryColors[item.country] = color1.color || '';
    }
  });

  const renderChart = (chartElement: HTMLElement) => {
    const chart = setUpChart(chartElement, { alt, margin: 4 });
    const { selection, sizes } = chart;

    addGradients(gradients);

    let projection = geoOrthographic()
      .scale(325)
      .rotate([0, -10])
      .translate([sizes.chart.width / 2, sizes.chart.height / 2]);
    const graticule = geoGraticule();
    const unkownFeatureData = geoData as unknown;
    const featureData: Topology = unkownFeatureData as Topology;
    const land = feature(featureData, featureData.objects.countries1) as ExtendedFeatureCollection<
      ExtendedFeature<any, GeoProperties>
    >;
    let path = geoPath(projection);

    selection.style('cursor', 'grab');

    // Draw globe
    selection
      .append('circle')
      .attr('class', 'globe')
      .attr('fill', '#000000')
      .attr('cx', sizes.chart.width / 2)
      .attr('cy', sizes.chart.height / 2)
      .attr('r', projection.scale());

    // Draw graticule
    selection
      .append('path')
      .datum(graticule())
      .attr('class', 'graticule')
      .attr('d', path)
      .attr('fill', 'none')
      .attr('stroke', '#404040')
      .attr('stroke-width', '1px');

    // Draw countries
    selection
      .selectAll('.country')
      .data(land.features)
      .enter()
      .append('path')
      .attr('class', (d) => {
        const country = d.properties.name;

        return `country ${country}`;
      })
      .attr('d', path)
      .attr('data-selected', (d) => {
        const country = d.properties.name;

        return isSelected(country) ? 'selected' : '';
      })
      .style('fill', (d) => {
        const country = d.properties.name;
        const selected = isSelected(country);

        if (selected && countryColors[country]) return countryColors[country];

        return '#666666';
      })
      .style('stroke', '#000000')
      .style('transition', 'opacity .2s ease-in')
      .style('cursor', (d) => {
        const country = d.properties.name;
        const selected = isSelected(country);

        return selected ? 'pointer' : 'drag';
      })
      .on('mouseout', function () {
        toolTip.hide();

        const graticleEl = window.document.querySelector('svg .graticule') as HTMLElement;

        if (graticleEl) graticleEl.style.opacity = '1';

        window.document.querySelectorAll('svg .country').forEach((el) => {
          const countryElement = el as HTMLElement;

          countryElement.style.opacity = '1';
        });
      })
      .on('mouseover', function (event, d) {
        const country = d.properties.name;
        const selected = isSelected(country);

        if (selected) {
          const graticleEl = window.document.querySelector('svg .graticule') as HTMLElement;
          if (graticleEl) graticleEl.style.opacity = '0.2';

          window.document?.querySelectorAll('svg .country').forEach((el) => {
            const countryElement = el as HTMLElement;

            if (!countryElement.isEqualNode(event.target)) countryElement.style.opacity = '0.2';
          });
        }
      })
      .on('mousemove', function (event, d: any) {
        const country = d.properties.name;
        const selected = isSelected(country);

        if (selected) {
          const index = items.findIndex((item) => item.country === country);
          const data = items[index];
          const list = data.additional
            ?.split(',')
            .map((project) => `<p>${project?.trim()}</p>`)
            .join('\n');

          const markup = `
          <p class="category">${country}</p>
          <p>${list}</p>
        `;

          toolTip.show(event.clientX, event.clientY, markup);
        }
      });

    // Draggable Globe
    const draggableGlobe: any = d3
      .drag()
      .on('drag', (event) => {
        const rotate = projection.rotate();
        const offset = sensitivity / projection.scale();
        const rotateX = rotate[0] + event.dx * offset;
        const rotateY = clamp(rotate[1] - event.dy * offset, -90, 90);

        projection.rotate([rotateX, rotateY]);
        path = geoPath().projection(projection);

        selection.selectAll('path').attr('d', function (d) {
          const datum = d as ExtendedFeature;

          return path(datum);
        });
      })
      .on('start', () => {
        selection.style('cursor', 'grabbing');
      })
      .on('end', () => {
        selection.style('cursor', 'grab');
      });

    selection.call(draggableGlobe);
  };
</script>

<ToolTip />
<ChartLayout
  {footnote}
  {headline}
  {id}
  {subhead}
  {renderChart}
  {encodeDataAttribute}
  {path}
  {gridColspan}
>
  {#snippet legend()}
    <Legend {categories} {colorPalette} {store} {gridColspan} />
  {/snippet}
  {#snippet table()}
    <Table {...tableProps} {gridColspan} />
  {/snippet}
</ChartLayout>
